/**
 * Spend by category, by member, and over time — three views of the same filtered data,
 * with the table underneath (DESIGN.md §12.7). Not a dashboard for its own sake.
 *
 * The bars are plain divs: no chart library for what is a sorted list with widths.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PageScroll } from "../../components/layout/PageScroll";
import { Spinner } from "../../components/ui/spinner";
import { useGroup, useGroupId } from "../../components/layout/AppShell";
import { api, type Stats } from "../../lib/api";
import { serializeFilters, parseFilters } from "../../lib/filters";
import { formatMoney } from "../../lib/money";
import { cn } from "../../lib/utils";

type GroupBy = "category" | "member" | "month";

const TABS: [GroupBy, string][] = [
  ["category", "By category"],
  ["member", "By member"],
  ["month", "Over time"],
];

export function StatsPage() {
  const groupId = useGroupId();
  const group = useGroup(groupId);
  const [searchParams] = useSearchParams();
  const filters = parseFilters(searchParams);
  const [groupBy, setGroupBy] = useState<GroupBy>("category");

  const params = serializeFilters(filters);
  params.set("group_by", groupBy);

  const stats = useQuery({
    queryKey: ["stats", groupId, params.toString()],
    queryFn: () => api.get<Stats>(`/groups/${groupId}/stats?${params}`),
  });

  const currency = group.data?.base_currency ?? "USD";
  const buckets = stats.data?.buckets ?? [];
  const peak = Math.max(1, ...buckets.map((bucket) => bucket.total_base_cents));
  const exportHref = `/api/groups/${groupId}/export.csv?${serializeFilters(filters)}`;

  return (
    <PageScroll>
      <div>
        <div className="flex items-center justify-between py-3">
          <h1 className="display text-sm text-muted">Stats</h1>
          <a className="text-xs text-accent underline underline-offset-2" href={exportHref}>
            Export CSV
          </a>
        </div>

        <div className="mb-4 flex gap-1">
          {TABS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setGroupBy(value)}
              aria-pressed={groupBy === value}
              className={cn("chip tap", groupBy === value && "chip-on")}
            >
              {label}
            </button>
          ))}
        </div>

        {stats.isPending ? (
          <Spinner />
        ) : buckets.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">No spending in this view yet.</p>
        ) : (
          <>
            <ul className="mb-6 flex flex-col gap-2" aria-label={`Spend ${groupBy}`}>
              {buckets.map((bucket) => (
                <li key={bucket.key}>
                  <div className="mb-0.5 flex items-baseline justify-between gap-3 text-sm">
                    <span className="truncate">{bucket.label}</span>
                    <span className="num">{formatMoney(bucket.total_base_cents, currency)}</span>
                  </div>
                  <div className="h-2 w-full bg-ink/[0.06]">
                    <div
                      className="h-full bg-accent"
                      style={{ width: `${(bucket.total_base_cents / peak) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-rule text-left text-2xs uppercase tracking-wider text-muted">
                  <th className="py-2 font-normal">{groupBy}</th>
                  <th className="py-2 text-right font-normal">Entries</th>
                  <th className="py-2 text-right font-normal">Total</th>
                  <th className="py-2 text-right font-normal">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {buckets.map((bucket) => (
                  <tr key={bucket.key}>
                    <td className="py-2">{bucket.label}</td>
                    <td className="num py-2 text-right">{bucket.transaction_count}</td>
                    <td className="num py-2 text-right">
                      {formatMoney(bucket.total_base_cents, currency)}
                    </td>
                    <td className="num py-2 text-right text-muted">
                      {stats.data
                        ? `${((bucket.total_base_cents / (stats.data.total_base_cents || 1)) * 100).toFixed(1)}%`
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-rule font-medium">
                  <td className="py-2">Total</td>
                  <td />
                  <td className="num py-2 text-right">
                    {formatMoney(stats.data?.total_base_cents ?? 0, currency)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>
    </PageScroll>
  );
}
